import React, { useEffect, useState } from 'react';
import './ReleasePanel.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { compareParticipantGroups } from '../../util/participantGroups';
import ProcessNotice from './ProcessNotice';
import {
  apiErrorMessage, eventNouns, eventShortLabel, formatDateTime, isOpenEvent, normalizeStepKind,
  releaseLabel, STEP_KIND,
} from './processUtils';

const REVOKE_POPUP = 'processrevokerelease';

/**
 * Step kind Release: GET Steps/{stepId}/Preview (affected events, events that stay invisible without a
 * leader, warnings/errors) → POST Execute. Additive, never downgrades, re-executable. "Freigabe
 * zurücknehmen" (POST Revoke) removes only the releases this step created.
 */
function ReleasePanel({
  process, step, active, activeActions, resetAction, onStartMode, onFinished, onProcess,
  groupsById, reportError, onNavigateStep,
}) {
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Warnings of the last execution in this session (e.g. "bleibt unsichtbar …").
  const [lastWarnings, setLastWarnings] = useState([]);

  const nouns = eventNouns(process);
  const events = process.events ?? [];
  const eventsById = new Map(events.map(e => [e.id, e]));
  const targetGroups = (step.targets ?? [])
    .map(t => groupsById.get(Number(t.participantGroupId)))
    .filter(Boolean)
    .sort(compareParticipantGroups);
  const leaderStep = (process.steps ?? []).find(s => normalizeStepKind(s.kind) === STEP_KIND.AssignLeaders) ?? null;
  const coveredEvents = events.filter(e => (e.coveredStepIds ?? []).map(Number).includes(Number(step.stepId)));

  // Reload the preview whenever something it depends on changes (events, their state, leaders).
  const signature = events.filter(isOpenEvent).map(e => `${e.id}:${e.state}:${(e.leaders ?? []).length}`).join(',');

  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    setLoading(true);
    setLoadError('');
    api.get(`/EventProcess/${process.id}/Steps/${step.stepId}/Preview`)
      .then(r => { if (alive) setPreview(r.data); })
      .catch(err => {
        if (!alive) return;
        setPreview(null);
        setLoadError(apiErrorMessage(err, 'Vorschau konnte nicht geladen werden.'));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [active, process.id, step.stepId, signature]);

  const handleExecute = () => {
    if (saving) return;
    setSaving(true);
    setError('');
    startSuccessBarLoading('Events werden freigegeben');
    api.post(`/EventProcess/${process.id}/Steps/${step.stepId}/Execute`, {})
      .then(response => {
        const warnings = response.data.warnings ?? [];
        startSuccessBarSuccess('Freigegeben');
        setLastWarnings(warnings);
        onFinished(response.data.process, warnings);
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorMessage(err, 'Freigabe fehlgeschlagen.'));
      })
      .finally(() => setSaving(false));
  };

  const handleRevoke = () => {
    startSuccessBarLoading('Freigabe wird zurückgenommen');
    api.post(`/EventProcess/${process.id}/Steps/${step.stepId}/Revoke`)
      .then(response => {
        startSuccessBarSuccess('Freigabe zurückgenommen');
        setLastWarnings([]);
        onProcess(response.data, { resetView: false });
      })
      .catch(err => {
        closeSuccessBar();
        reportError(apiErrorMessage(err, 'Freigabe konnte nicht zurückgenommen werden.'));
      });
  };

  const targetChips = targetGroups.length > 0 && (
    <div className={'processReleaseTargets'}>
      <span className={'processReleaseLabel'}>Freigabe für</span>
      {targetGroups.map(g => (
        <ParticipantGroupChip key={g.id} group={g} label={g.isGuest ? 'Öffentlich (Gäste)' : releaseLabel(g)} />
      ))}
    </div>
  );

  const revokePopup = (
    <Popup
      name={REVOKE_POPUP}
      confirmText={'Zurücknehmen'}
      headerText={'Freigabe zurücknehmen'}
      type={'confirmaction'}
      onConfirm={handleRevoke}
      withCancel
    >
      <p>
        Die Freigaben, die der Schritt <b>„{step.label}"</b> gesetzt hat
        {targetGroups.length > 0 ? <> (für <b>{targetGroups.map(releaseLabel).join(', ')}</b>)</> : null}, werden entfernt:
      </p>
      {coveredEvents.length > 0 ? (
        <ul className={'processReleaseRevokeList'}>
          {coveredEvents.map(e => (
            <li key={e.id}>
              <span>{eventShortLabel(e)}</span>
              <span className={'processReleaseRevokeCount'}>{e.confirmedCount} Anmeldungen</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={'formFieldHint'}>Keine Events mit Freigaben aus diesem Schritt.</p>
      )}
      <p className={'formFieldHint'}>
        Manuelle Freigaben aus dem Bearbeiten-Popup bleiben. Bestehende Anmeldungen bleiben bestehen. Der Schritt
        wird wieder auf „Nicht erledigt" gesetzt.
      </p>
    </Popup>
  );

  // A reset ("Nicht erledigt") or a later skip keeps the releases of an earlier run — they stay
  // revocable in every status (the reset popup points to "Freigabe zurücknehmen").
  const leftoverText = `${coveredEvents.length} ${coveredEvents.length === 1 ? 'Event ist' : 'Events sind'} aus einer früheren Ausführung noch über diesen Schritt freigegeben.`;
  const revokeButton = (
    <ActionButton icon={'visibility_off'} label={'Freigabe zurücknehmen'} type={1} onClick={() => openPopup(REVOKE_POPUP, step.stepId)} />
  );

  if (!active) {
    const skipped = step.status === 'skipped';
    return (
      <React.Fragment>
        {targetChips}
        <p className={'processActionDesc'}>
          {skipped
            ? (coveredEvents.length > 0
              ? `Dieser Schritt wurde übersprungen – ${leftoverText}`
              : 'Dieser Schritt wurde übersprungen – die Events sind über ihn nicht freigegeben.')
            : `${coveredEvents.length} ${coveredEvents.length === 1 ? 'Event' : 'Events'} über diesen Schritt freigegeben${step.executedAt ? ` (zuletzt am ${formatDateTime(step.executedAt)})` : ''}. Später hinzugefügte Entwürfe werden automatisch nachgezogen.`}
        </p>
        {lastWarnings.length > 0 && (
          <ProcessNotice tone={'warning'}>
            <ul>{lastWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </ProcessNotice>
        )}
        <div className={'processActionButtons'}>
          <ActionButton icon={'visibility'} label={skipped ? 'Jetzt freigeben' : 'Erneut ausführen'} type={3} onClick={() => onStartMode('run')} />
          {(!skipped || coveredEvents.length > 0) && revokeButton}
          {resetAction}
        </div>
        {revokePopup}
      </React.Fragment>
    );
  }

  const previewEvents = (preview?.eventIds ?? []).map(id => eventsById.get(id)).filter(Boolean);
  const withoutLeader = new Set((preview?.eventsWithoutLeader ?? []).map(Number));
  const errors = preview?.errors ?? [];
  const warnings = preview?.warnings ?? [];

  return (
    <React.Fragment>
      <p className={'processActionDesc'}>
        Die {nouns.many} werden für die Zielgruppen freigegeben: Sie werden für diese Gruppen sichtbar und buchbar.
        Bestehende Freigaben bleiben erhalten; abgesagte und abgeschlossene {nouns.many} bleiben unverändert.
      </p>
      {targetChips}
      {error && <ProcessNotice tone={'error'}>{error}</ProcessNotice>}
      {loadError && <ProcessNotice tone={'error'}>{loadError}</ProcessNotice>}
      {errors.map((e, i) => <ProcessNotice key={`e${i}`} tone={'error'}>{e}</ProcessNotice>)}
      {warnings.map((w, i) => <ProcessNotice key={`w${i}`} tone={'warning'}>{w}</ProcessNotice>)}
      {step.status === 'pending' && coveredEvents.length > 0 && (
        <ProcessNotice tone={'info'} actions={revokeButton}>{leftoverText}</ProcessNotice>
      )}

      {loading && !preview ? (
        <p className={'processEmpty'}>Vorschau wird geladen …</p>
      ) : previewEvents.length > 0 && (
        <ul className={'processReleaseEvents'} aria-label={'Betroffene Events'}>
          {previewEvents.map(e => (
            <li key={e.id} className={`processReleaseEvent${withoutLeader.has(e.id) ? ' isWithoutLeader' : ''}`}>
              <span className={'processReleaseEventLabel'}>{eventShortLabel(e)}</span>
              {withoutLeader.has(e.id) && (
                <span className={'processReleaseEventHint'}>
                  <span className={'material-symbols-rounded'} aria-hidden={'true'}>visibility_off</span>
                  bleibt unsichtbar, bis eine Spieltagleitung eingetragen ist
                  {leaderStep && typeof onNavigateStep === 'function' && (
                    <button type={'button'} className={'processReleaseLink'} onClick={() => onNavigateStep(leaderStep.key)}>
                      Leitung zuweisen
                    </button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className={'processActionButtons'}>
        <ActionButton
          icon={'visibility'}
          label={'Freigeben'}
          type={2}
          onClick={handleExecute}
          disabled={saving || loading || !preview || errors.length > 0}
        />
        {activeActions}
      </div>
      {revokePopup}
    </React.Fragment>
  );
}

export default ReleasePanel;
